import {Expressions} from '@packages/systems/wfdl';
import {type Expression} from '@packages/systems/wfdl-ast';
import {
  EventAppliesTo,
  ActionAppliesTo,
} from '@packages/systems/ix2/shared-constants';
import {getNodeIdFromIx2Id} from '@packages/systems/ix2/shared-utils';
import type {
  ActionListsType,
  ActionListType,
  ActionItemType,
  EventsType,
  EventType,
  InteractionsType,
  InteractionType,
  ContinuousParameterGroupType,
  IX2StoreStateType,
  IX2EventData,
  IX2Interaction,
  IX2List,
} from '@packages/systems/ix2/types-core';
import {toJS} from '@packages/systems/core/utils/ImmUtils';
import type {Ix2Type} from '../types';
import {
  MutableQualifiedMap,
  PluginName,
  Qualified,
  QualifiedMap,
  getPluginName,
} from '@packages/systems/wfdl-Qualified';
import {List, RecordOf} from 'immutable';

const handleActionItem = (
  actionItem: ActionItemType,
  actionList: ActionListType,
  ix2ActionListsMap: {
    [key: string]: ActionListsType;
  },
  actionListIdToActionListMap: {
    [key: string]: ActionListType;
  },
  actionListsSet: Set<ActionListType>,
  styleSet: Set<string>
): void => {
  const nodeId = actionItem.config?.target?.nodeId;
  const appliesTo = actionItem.config?.target?.appliesTo;
  if (!nodeId || !appliesTo) return;

  if (appliesTo === ActionAppliesTo.ELEMENT) {
    const targetId = getNodeIdFromIx2Id(nodeId);
    const targetIdActionItems = ix2ActionListsMap[targetId] || [];
    targetIdActionItems.push(actionList);
    ix2ActionListsMap[targetId] = targetIdActionItems;
  } else if (appliesTo === ActionAppliesTo.ELEMENT_CLASS) {
    const styleIds = actionItem.config?.target?.styleBlockIds || [];
    const isStyleIncluded = styleIds.some((id) => styleSet.has(id));
    if (isStyleIncluded) actionListsSet.add(actionList);
  } else if (appliesTo === ActionAppliesTo.TRIGGER_ELEMENT) {
    actionListIdToActionListMap[actionList.id] = actionList;
  }
};

export const getInteractionsFromComponentRender = (
  render: Expression,
  ix2: Ix2Type,
  styleBlockIds: string[]
): Ix2Type => {
  const elementIds = Object.keys(Expressions.allElements(render));
  return getInteractionsFromTargets(ix2, {elementIds, styleBlockIds});
};

export const getInteractionsFromTargets = (
  ix2: Ix2Type,
  targetIds: {
    elementIds?: string[];
    styleBlockIds?: string[];
  }
): Ix2Type => {
  const {styleBlockIds = [], elementIds = []} = targetIds;
  const styleSet = new Set(styleBlockIds);
  const interactionsSet = new Set<InteractionType>();
  const eventSet = new Set<EventType>();
  const actionListsSet = new Set<ActionListType>();
  const ix2InteractionsMap: Record<string, InteractionsType> = {};
  const ix2EventsMap: Record<string, EventsType> = {};
  const ix2ActionListsMap: Record<string, ActionListsType> = {};
  const actionListIdToActionListMap: Record<string, ActionListType> = {};
  const eventsToInteractionsMap: Record<string, InteractionsType> = {};

  ix2.interactions.forEach((interaction) => {
    const {target} = interaction;
    if (!target) throw Error('expected target id string');

    const targetId = getNodeIdFromIx2Id(target);
    const targetIdInteractions = ix2InteractionsMap[targetId] || [];
    targetIdInteractions.push(interaction);
    ix2InteractionsMap[targetId] = targetIdInteractions;

    const eventIds = interaction.eventIds || [];
    eventIds.forEach((eventId) => {
      if (eventId in eventsToInteractionsMap) {
        eventsToInteractionsMap[eventId]?.push(interaction);
      } else {
        eventsToInteractionsMap[eventId] = [interaction];
      }
    });
  });

  ix2.events.forEach((event) => {
    if (!event.target) return;
    const allTargets = new Set([...(event.targets || []), event.target]);

    allTargets.forEach((eventTarget) => {
      if (eventTarget.appliesTo === EventAppliesTo.ELEMENT) {
        if (!eventTarget?.id) throw Error('expected target id string');
        const targetId = getNodeIdFromIx2Id(eventTarget.id);
        const targetIdEvents = ix2EventsMap[targetId] || [];
        targetIdEvents.push(event);
        ix2EventsMap[targetId] = targetIdEvents;
      } else if (eventTarget.appliesTo === EventAppliesTo.CLASS) {
        const styleIds = eventTarget.styleBlockIds || [];
        const isStyleIncluded = styleIds.some((id) => styleSet.has(id));
        if (isStyleIncluded) eventSet.add(event);
      }
    });
  });

  ix2.actionLists.forEach((actionList) => {
    // Handles Timed action lists
    if (
      'actionItemGroups' in actionList &&
      Array.isArray(actionList.actionItemGroups) &&
      actionList.actionItemGroups.length > 0
    ) {
      actionList.actionItemGroups.forEach(({actionItems}) => {
        actionItems.forEach((actionItem) => {
          handleActionItem(
            actionItem,
            actionList,
            ix2ActionListsMap,
            actionListIdToActionListMap,
            actionListsSet,
            styleSet
          );
        });
      });
      return;
    }

    // Handles Continuous action lists
    if (
      'continuousParameterGroups' in actionList &&
      Array.isArray(actionList.continuousParameterGroups) &&
      actionList.continuousParameterGroups.length > 0
    ) {
      actionList.continuousParameterGroups.forEach(
        ({continuousActionGroups}) => {
          continuousActionGroups.forEach(({actionItems}) => {
            actionItems.forEach((actionItem) => {
              handleActionItem(
                actionItem,
                actionList,
                ix2ActionListsMap,
                actionListIdToActionListMap,
                actionListsSet,
                styleSet
              );
            });
          });
        }
      );
      return;
    }
  });

  elementIds.forEach((id) => {
    if (id in ix2InteractionsMap) {
      ix2InteractionsMap[id]?.forEach(interactionsSet.add, interactionsSet);
    }
    if (id in ix2EventsMap) {
      ix2EventsMap[id]?.forEach(eventSet.add, eventSet);
    }
    if (id in ix2ActionListsMap) {
      ix2ActionListsMap[id]?.forEach(actionListsSet.add, actionListsSet);
    }
  });

  // events & actionLists map to each other via the actionListId property.
  // if an actionList applies to a trigger element, the relevant element id
  // exists in the event the actionList maps to. here we look through the element id's in
  // relevant events to determine if the actionList applies to an included trigger element.

  eventSet.forEach((event) => {
    const interactions = eventsToInteractionsMap[event.id];
    if (interactions) {
      interactions.forEach(interactionsSet.add, interactionsSet);
    }
    const actionListId = event?.action?.config?.actionListId;
    const actionList = actionListId
      ? actionListIdToActionListMap[actionListId]
      : null;
    if (actionList) {
      actionListsSet.add(actionList);
    }
  });

  return {
    interactions: Array.from(interactionsSet),
    events: Array.from(eventSet),
    actionLists: Array.from(actionListsSet),
  };
};

/**
 * Utility that creates an origin map {[origin] => IX2Object}
 */
function buildInteractionsOriginMap<T extends {origin?: Qualified<string>}>(
  items: T[]
): QualifiedMap<string, T> {
  const originMap_mut = new MutableQualifiedMap() satisfies QualifiedMap<
    string,
    T
  >;

  items.forEach((item) => {
    if (item.origin) {
      originMap_mut.set_mut(item.origin, item);
    }
  });

  return originMap_mut.asNonMutableCopy();
}

/**
 * Utility method to update actionList id references in events
 */
function updateIdsInInteractionEvents<E extends EventType>(
  events: E[],
  actionListIdMap: {[id: string]: string},
  continuousParameterGroupIdMap: {[id: string]: string}
): E[] {
  return events.map((event) => {
    if (!event.origin) {
      // Don't update non-library events
      return event;
    }

    const actionListId = event.action?.config?.actionListId;
    const newActionListId = actionListId ? actionListIdMap[actionListId] : null;
    if (event.action?.config && newActionListId) {
      // If we find a corresponding actionListId, we update the event object
      event = {
        ...event,
        action: {
          ...event.action,
          config: {
            ...event.action.config,
            actionListId: newActionListId,
          },
        },
      };
    }

    if (Array.isArray(event.config)) {
      const nextConfig = event.config.map((config) => {
        const continuousParameterGroupId = config.continuousParameterGroupId;
        const newContinuousParameterGroupId = continuousParameterGroupId
          ? continuousParameterGroupIdMap[continuousParameterGroupId]
          : null;
        if (newContinuousParameterGroupId) {
          // If we find a corresponding continuousParameterGroupId,
          // we update the event object
          return {
            ...config,
            continuousParameterGroupId: newContinuousParameterGroupId,
          };
        }

        return config;
      });

      event = {
        ...event,
        config: nextConfig,
      };
    }

    return event;
  });
}

/**
 * Utility method to update event id references in interactions
 */
function updateIdsInInteractions<I extends InteractionType>(
  interactions: I[],
  eventIdMap: {[id: string]: string}
) {
  return interactions.map((interaction) => {
    if (!interaction.origin) {
      // Don't update non-library interactions
      return interaction;
    }

    if (!Array.isArray(interaction.eventIds)) {
      // Nothing to update
      return interaction;
    }

    const nextIds = interaction.eventIds.map((eventId) => {
      return eventIdMap[eventId] || eventId;
    }) as I['eventIds'];

    return {
      ...interaction,
      eventIds: nextIds,
    };
  });
}

/**
 * Utility method that returns a function that collects idMaps
 * for an actionList pair by mutating the given maps
 */
function collectInteractionActionListIdMaps<A extends ActionListType>({
  actionListIdMap_mut,
  continuousParameterGroupIdMap_mut,
}: {
  /**
   * IdMap for actionListId [mutable]
   */
  actionListIdMap_mut: {[id: string]: string};
  /**
   * IdMap for continuousParameterGroupId [mutable]
   */
  continuousParameterGroupIdMap_mut: {[id: string]: string};
}) {
  return (actionList: A, actionList_2: A) => {
    const actionListId = actionList.id;
    const actionListId_2 = actionList_2.id;
    if (actionListId && actionListId_2) {
      actionListIdMap_mut[actionListId] = actionListId_2;
    }

    const parameterGroups: ContinuousParameterGroupType<
      'MOUSE_X' | 'MOUSE_Y' | 'SCROLL_PROGRESS'
    >[] =
      ('continuousParameterGroups' in actionList &&
        actionList.continuousParameterGroups) ||
      [];
    const parameterGroups_2: ContinuousParameterGroupType<
      'MOUSE_X' | 'MOUSE_Y' | 'SCROLL_PROGRESS'
    >[] =
      ('continuousParameterGroups' in actionList_2 &&
        actionList_2.continuousParameterGroups) ||
      [];

    parameterGroups.forEach((parameterGroup, index) => {
      const paramGroupId = parameterGroup.id;
      const paramGroupId_2 = parameterGroups_2[index]?.id;
      if (paramGroupId && paramGroupId_2) {
        continuousParameterGroupIdMap_mut[paramGroupId] = paramGroupId_2;
      }
    });
  };
}

/**
 * Utility method that returns a function that collects idMaps
 * for an event pair by mutating the given map
 */
function collectInteractionEventIdMaps<E extends EventType>({
  eventIdMap_mut,
}: {
  /**
   * IdMap for eventsId [mutable]
   */
  eventIdMap_mut: {[id: string]: string};
}) {
  return (event: E, event_2: E) => {
    const eventId = event.id;
    const eventId_2 = event_2.id;
    if (eventId && eventId_2) {
      eventIdMap_mut[eventId] = eventId_2;
    }
  };
}

type IX2Data<
  I extends InteractionType,
  E extends EventType,
  A extends ActionListType,
> = {
  interactions: I[];
  events: E[];
  actionLists: A[];
};
type IX2Item = ActionListType | EventType | InteractionType;
type GetOriginFn = (item: IX2Item) => Qualified<string> | undefined;
type GetExistingOriginItemFn<T extends IX2Item> = (
  origin: Qualified<string>
) => T | undefined;

function processIx2Data<T extends IX2Item>(
  /**
   * Incoming ix2 items that we want to process
   */
  incomingIx2Data: T[],
  /**
   * A callback function to get the `origin` from an ix2 item
   */
  getOriginFromIx2Item: GetOriginFn,
  /**
   * A callback function to get the existing item by origin
   */
  getExistingOriginItem: GetExistingOriginItemFn<T>,
  /**
   * A callback function to process a pair of omitted ix2 items
   */
  processOmittedItems?: (item_1: T, item_2: T) => void
): T[] {
  return incomingIx2Data.reduce((acc, item) => {
    const origin = getOriginFromIx2Item(item);
    const matchingOriginItem = origin
      ? getExistingOriginItem(origin)
      : undefined;
    if (matchingOriginItem) {
      // If an interaction object with the same origin already exists in state,
      // then we omit it because we don't want to duplicate it.
      processOmittedItems?.(item, matchingOriginItem);
    } else {
      // Else we add it, with the origin set
      acc.push({
        ...item,
        origin,
      });
    }
    return acc;
  }, [] as T[]);
}

export function deduplicateIx2Data<
  I extends InteractionType,
  E extends EventType,
  A extends ActionListType,
>({
  incomingIx2Data,
  exitingIx2Data,
  getOriginFromIx2Item,
}: {
  incomingIx2Data: IX2Data<I, E, A>;
  exitingIx2Data: IX2Data<I, E, A>;
  getOriginFromIx2Item: GetOriginFn;
}): IX2Data<I, E, A> {
  // 0. We build the needed maps
  const eventIdMap_mut: {[id: string]: string} = {};
  const actionListIdMap_mut: {[id: string]: string} = {};
  const continuousParameterGroupIdMap_mut: {[id: string]: string} = {};

  const actionListOriginMap = buildInteractionsOriginMap<A>(
    exitingIx2Data.actionLists
  );
  const eventsOriginMap = buildInteractionsOriginMap<E>(exitingIx2Data.events);
  const interactionsOriginMap = buildInteractionsOriginMap<I>(
    exitingIx2Data.interactions
  );

  // 1. First we deduplicate action lists and we collect
  // the necessary id maps
  const nextActionLists = processIx2Data<A>(
    incomingIx2Data.actionLists,
    getOriginFromIx2Item,
    (origin) => actionListOriginMap.get(origin),
    collectInteractionActionListIdMaps({
      actionListIdMap_mut,
      continuousParameterGroupIdMap_mut,
    })
  );

  // 2. Then we deduplicate events..
  let nextEvents = processIx2Data<E>(
    incomingIx2Data.events,
    getOriginFromIx2Item,
    (origin) => eventsOriginMap.get(origin),
    collectInteractionEventIdMaps({eventIdMap_mut})
  );

  // 2.5 ..and update id references
  nextEvents = updateIdsInInteractionEvents<E>(
    nextEvents,
    actionListIdMap_mut,
    continuousParameterGroupIdMap_mut
  );

  // 3. Finally we deduplicate interactions..
  let nextInteractions = processIx2Data<I>(
    incomingIx2Data.interactions,
    getOriginFromIx2Item,
    (origin) => interactionsOriginMap.get(origin)
  );

  // 3.5 ..and update id references
  nextInteractions = updateIdsInInteractions<I>(
    nextInteractions,
    eventIdMap_mut
  );

  return {
    actionLists: nextActionLists,
    events: nextEvents,
    interactions: nextInteractions,
  };
}

export function subtractIx2Data<
  I extends InteractionType,
  E extends EventType,
  A extends ActionListType,
>({
  incomingIx2Data,
  ix2DataToSubtract,
}: {
  incomingIx2Data: IX2Data<I, E, A>;
  ix2DataToSubtract: IX2Data<I, E, A>;
}): IX2Data<I, E, A> {
  const nextActionLists = incomingIx2Data.actionLists.filter(
    (actionList) =>
      !ix2DataToSubtract.actionLists.some(
        (actionList_2) => actionList_2.id === actionList.id
      )
  );
  const nextEvents = incomingIx2Data.events.filter(
    (event) =>
      !ix2DataToSubtract.events.some((event_2) => event_2.id === event.id)
  );
  const nextInteractions = incomingIx2Data.interactions.filter(
    (interaction) =>
      !ix2DataToSubtract.interactions.some(
        (interaction_2) => interaction_2.id === interaction.id
      )
  );

  return {
    actionLists: nextActionLists,
    events: nextEvents,
    interactions: nextInteractions,
  };
}

/**
 * Delete any IX2 data that belongs to any plugin that was uninstalled:
 */
export function deleteIx2ByOrigin(
  pluginNames: PluginName[],
  ix2State: IX2StoreStateType
): IX2StoreStateType {
  const pluginNamesSet = new Set(pluginNames);
  const omitItemsFromPlugin = <T extends {origin?: Qualified<string>}>(
    items: List<T>
  ): List<T> =>
    items.filter(
      (item) =>
        item.origin == null || !pluginNamesSet.has(getPluginName(item.origin))
    );

  return ix2State
    .update('actionLists', omitItemsFromPlugin)
    .update('events', omitItemsFromPlugin)
    .update('interactions', omitItemsFromPlugin);
}

/**
 * Delete any IX2 data from state:
 */
export function removeIx2DataFromState(
  ix2DataToRemove: IX2Data<InteractionType, EventType, ActionListType>,
  ix2State: IX2StoreStateType
): IX2StoreStateType {
  const omitItemsFromState =
    <T extends {id: string}>(
      itemsToRemove: Array<InteractionType | EventType | ActionListType>
    ) =>
    (items: List<T>): List<T> =>
      items.filter(
        (item) => !itemsToRemove.some((item_2) => item_2.id === item.id)
      );

  return ix2State
    .update('actionLists', omitItemsFromState(ix2DataToRemove.actionLists))
    .update('events', omitItemsFromState(ix2DataToRemove.events))
    .update('interactions', omitItemsFromState(ix2DataToRemove.interactions));
}

/**
 * Get all IX2 data (in POJO format) where `origin` equals the given pluginName.
 */
export function getIx2Data(
  ix2State: IX2StoreStateType,
  pluginNames?: PluginName[]
): Ix2Type {
  const reduceByOriginPlugin = <
    InputType extends {origin?: Qualified<string>},
    ReturnType extends EventType | InteractionType | ActionListType,
  >(
    accumulator: ReturnType[],
    item: InputType
  ): ReturnType[] => {
    const pluginNamesSet = pluginNames?.length ? new Set(pluginNames) : null;
    if (
      // no filtering if no pluginName is provided
      !pluginNamesSet ||
      // or filter by origin otherwise
      (item.origin && pluginNamesSet.has(getPluginName(item.origin)))
    ) {
      accumulator.push(toJS(item));
    }
    return accumulator;
  };

  const events = ix2State
    .get('events')
    .reduce(reduceByOriginPlugin<RecordOf<IX2EventData>, EventType>, []);
  const interactions = ix2State
    .get('interactions')
    .reduce(reduceByOriginPlugin<IX2Interaction, InteractionType>, []);
  const actionLists = ix2State
    .get('actionLists')
    .reduce(reduceByOriginPlugin<IX2List, ActionListType>, []);

  return {
    events,
    interactions,
    actionLists,
  };
}
